import { useCallback, useEffect, useRef } from 'react';

import {
	CancelError,
	CancellablePromise,
	createClient,
	fetchTeamsBulk,
	isSearchCompassPartial,
	isSearchResultJiraIssue,
	resultsCountQuery,
	type SearchPageResponse,
} from '@atlassian/dynamic-cards-search-client';

import {
	DEFAULT_ENTITY_ATI,
	RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS,
	RESULT_LIMIT_FOR_SINGLE_PRODUCT,
} from '../../common/constants/entities';
import { searchResultsLoadUFOExperience } from '../../common/ui/ufo/experiences';
import { isProduct3P } from '../../common/utils/product';
import { getTeamId } from '../../common/utils/teams';
import { useLogException } from '../error-boundary';
import { useSelectedFilters } from '../filters/selected';
import { useSafeIntercom } from '../intercom';
import {
	useAutoCorrection,
	useBackendExperiment,
	useBootstrap,
	useFetchFromCursor,
	useFrontendExperiments,
	useIsSearchReady,
	useResultsSort,
	useRetryCount,
	useSearchActions,
	useSearchQuery,
	useSearchSessionId,
	useSelectedProduct,
} from '../store';
import { useOriginalQuery } from '../store/search';

import {
	EmptyEntitiesError,
	fetchJiraFields,
	fetchResults,
	ResultsFetchError,
} from './fetch-results';

export const useFetchAutoCorrection = (): void => {
	const [{ cloudId = '', aggregatorHostName }] = useBootstrap();
	const [searchQuery] = useSearchQuery();
	const [, { setAutoCorrection }] = useAutoCorrection();

	useEffect(() => {
		const searchClient = createClient({
			cloudId,
			aggregatorHostName,
		});
		const containsSearchQuery = searchQuery !== '';
		const shouldGetAutoCorrection = containsSearchQuery;

		if (!shouldGetAutoCorrection) {
			return;
		}

		searchClient.getAutoCorrection({ query: searchQuery }).then((autoCorrection) => {
			setAutoCorrection(autoCorrection);
		});
	}, [searchQuery, cloudId, setAutoCorrection, aggregatorHostName]);
};

/**
 * Fetches search results using search parameters from the query
 * and filter stores. During and after requests, the results store is
 * updated accordingly. If multiple requests are fired only the last
 * request will cause the results store to be updated.
 */
export const useFetchResults = (): void => {
	const [{ cloudId = '', aggAbsoluteUrl, primaryProduct, outboundAuthConfigs }] = useBootstrap();
	const isSearchReady = useIsSearchReady();
	const [searchSessionId] = useSearchSessionId();
	const [{ order, field }] = useResultsSort();
	const [selectedProduct] = useSelectedProduct();
	const fetchFromCursor = useFetchFromCursor();
	const {
		selectedFilters,
		unselectedProductsThirdPartyFilters,
		selectedEntities,
		entities,
		hasFiltersSelected,
		entitiesForResultsCountQuery,
	} = useSelectedFilters({
		outboundAuthConfigs,
	});

	const [searchQuery] = useSearchQuery();
	const [originalQuery] = useOriginalQuery();
	const {
		setJiraIssueDetails,
		setJiraIssueFieldsLoading,
		setCompassTeamsLoading,
		setCompassTeams,
		setIsLoading,
		setResults,
		setQueryUpdatedTime,
		resetResults,
		setProductResultCount,
		setIsLoadingMore,
		appendMoreResults,
	} = useSearchActions();
	const [retryCount] = useRetryCount();
	const requestRef = useRef<CancellablePromise<SearchPageResponse>>();
	const [backendExperiment] = useBackendExperiment();
	const { experimentId, shadowExperimentId, experimentLayers } = backendExperiment || {};
	const intercom = useSafeIntercom();
	const [frontendExperiments] = useFrontendExperiments();
	const logException = useLogException();

	const trackIntercomEvents = useCallback(() => {
		if (selectedProduct && isProduct3P(selectedProduct)) {
			intercom?.trackEvent('search_requested_3p', { product_filter: selectedProduct });
		} else {
			intercom?.trackEvent('search_requested');
		}
	}, [selectedProduct, intercom]);

	// We compare these via deep value equality, as currently the objects are regenerated
	// The bug this fixes is that when adding a filter from a dropdown, we shouldn't cause a search to run or load
	// TODO refactor this so we can go back to Object.is dependency equality on these objects
	const selectedFiltersValueEquality = JSON.stringify(selectedFilters);
	const selectedEntitiesValueEquality = JSON.stringify(selectedEntities);
	const entitiesValueEquality = JSON.stringify(entities);
	const frontendExperimentsValueEquality = JSON.stringify(frontendExperiments);
	const experimentLayersValueEquality = JSON.stringify(experimentLayers);

	useEffect(() => {
		if (!isSearchReady) {
			return;
		}

		// Reset results when...
		// 1. There is no search query
		// 2. There is no selected product or the selected product is not Confluence
		// 3. There are no selected child filters
		const containsSearchQuery = searchQuery !== '';
		const confluenceIsSelected = selectedProduct === 'confluence';
		const shouldTriggerSearch =
			containsSearchQuery || (containsSearchQuery && hasFiltersSelected) || confluenceIsSelected;

		// Reset results and cancel inflight requests if we are not searching
		if (!shouldTriggerSearch) {
			if (requestRef.current) {
				requestRef.current.cancel();
			}
			resetResults();

			return;
		}

		// Cancel the inflight request if there is one
		if (requestRef.current) {
			requestRef.current.cancel();
		}
		setProductResultCount();

		const restartUFOExperience = async () => {
			if (requestRef.current) {
				await searchResultsLoadUFOExperience.abort();
			}
			await searchResultsLoadUFOExperience.start();
			searchResultsLoadUFOExperience.mark('request_start');
		};
		restartUFOExperience();

		if (fetchFromCursor && fetchFromCursor !== '') {
			setIsLoadingMore(true);
		} else {
			setIsLoading(true);
		}
		setQueryUpdatedTime();

		requestRef.current = new CancellablePromise(
			fetchResults({
				query: searchQuery,
				originalQuery,
				entities,
				selectedFilters,
				aggAbsoluteUrl,
				cloudId,
				searchSessionId,
				sort: { order, field },
				sourceProduct: primaryProduct,
				experimentId,
				shadowExperimentId,
				experimentLayers,
				after: fetchFromCursor,
				limit:
					entities.length === DEFAULT_ENTITY_ATI.length
						? RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS
						: RESULT_LIMIT_FOR_SINGLE_PRODUCT,
			}),
		);
		requestRef.current
			.promise()
			.then((aggResults) => {
				const { data, extensions, errors } = aggResults;
				const errorMessage = errors?.[0]?.message;
				const resultsFetchError = errorMessage ? new ResultsFetchError(errorMessage) : undefined;
				if (fetchFromCursor && fetchFromCursor !== '') {
					appendMoreResults(data?.search.results);
				} else {
					setResults(
						data?.search.results,
						resultsFetchError,
						extensions?.gateway?.request_id || undefined,
					);
				}
				setProductResultCount(data?.search.results.totalCounts || []);
				trackIntercomEvents();

				searchResultsLoadUFOExperience.mark('request_end');
				searchResultsLoadUFOExperience.mark('render_start');

				resultsFetchError && logException(resultsFetchError);

				const edges = data?.search.results.edges;
				// Fetch extra fields for Issues
				const issuesToFetch: string[] = [];
				// Fetch team names associated with the teamIds in Compass results
				const teamsIdsToFetch: string[] = [];
				if (edges && edges.length > 0) {
					edges.forEach((edge) => {
						if (isSearchResultJiraIssue(edge.node)) {
							const issueKey =
								edge.node.issue?.webUrl?.split('/').at(-1) || edge.node.url.split('/').at(-1);
							if (!!issueKey) {
								issuesToFetch.push(issueKey);
							}
						} else if (isSearchCompassPartial(edge.node)) {
							const teamId = getTeamId(edge.node.ownerId);
							if (teamId && !teamsIdsToFetch.includes(teamId)) {
								teamsIdsToFetch.push(teamId);
							}
						}
					});
				}

				if (issuesToFetch.length > 0) {
					setJiraIssueFieldsLoading(true);
					fetchJiraFields({ issueKeys: issuesToFetch, cloudId, aggAbsoluteUrl })
						.then((jiraHydrationResults) => {
							const issues = jiraHydrationResults.data?.jira.issuesByKey;
							setJiraIssueDetails(issues || []);
						})
						.finally(() => {
							setJiraIssueFieldsLoading(false);
						});
				}
				if (teamsIdsToFetch.length > 0) {
					setCompassTeamsLoading(true);
					fetchTeamsBulk({
						siteId: cloudId,
						teamIds: teamsIdsToFetch,
					})
						.then((data) => {
							setCompassTeams(data.teams);
						})
						.finally(() => {
							setCompassTeamsLoading(false);
						});
				}
			})
			.catch((e: Error) => {
				if (e instanceof EmptyEntitiesError) {
					setResults(undefined, e);
				} else if (!(e instanceof CancelError)) {
					setResults(undefined, new ResultsFetchError(e.message));
					setProductResultCount([]);
					trackIntercomEvents();
				}
			});

		const { commonFilters, confluenceFilters, jiraFilters } = selectedFilters;

		const resultsCountVariables = {
			query: searchQuery,
			queryVersion: 1,
			cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
			limit: 0,
			commonFilters,
			confluenceFilters,
			jiraFilters,
			thirdPartyFilters: unselectedProductsThirdPartyFilters,
			entities: entitiesForResultsCountQuery,
			searchSessionId,
			sort: [],
			sourceProduct: primaryProduct,
		};
		const showResultCounts = false;

		if (showResultCounts) {
			resultsCountQuery({
				variables: resultsCountVariables,
				aggAbsoluteUrl,
				originalQuery,
			})
				.then((resultsCountData) => {
					setProductResultCount(resultsCountData?.data?.search.results.totalCounts || []);
				})
				.catch(() => {
					setProductResultCount([]);
				});
		}
		// Note! Please ensure all dependencies are actually here, we're just trying to ignore the xValueEquality ones
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isSearchReady,
		resetResults,
		searchQuery,
		originalQuery,
		selectedFiltersValueEquality, // selectedFilters,
		selectedEntitiesValueEquality, // selectedEntities,
		entitiesValueEquality, // entities,
		selectedProduct,
		hasFiltersSelected,
		setIsLoading,
		setResults,
		setQueryUpdatedTime,
		setProductResultCount,
		searchSessionId,
		aggAbsoluteUrl,
		cloudId,
		retryCount, // will cause a new request when it increments. Is reset to 0 when query changes.
		field,
		order,
		primaryProduct,
		experimentId,
		experimentLayersValueEquality,
		shadowExperimentId,
		trackIntercomEvents,
		frontendExperimentsValueEquality,
		fetchFromCursor,
	]);
};
